//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { truncateToFourDecimalPlaces } from '@email-builder/utils/emailBuilderUtils';

export enum EMAIL_BUILDER_ACTIONS {
  // Customization actions
  UPDATE_OPTIONS = 'EMAIL_BUILDER/UPDATE_OPTIONS',
  UPDATE_OPTIONAL_FILTERS = 'EMAIL_BUILDER/UPDATE_OPTIONAL_FILTERS',
  UPDATE_LAYOUT_SETTINGS = 'EMAIL_BUILDER/UPDATE_LAYOUT_SETTINGS',
  UPDATE_SOCIAL_INFORMATION = 'EMAIL_BUILDER/UPDATE_SOCIAL_INFORMATION',
  UPDATE_MEDIA_TYPE_FILTER = 'EMAIL_BUILDER/UPDATE_MEDIA_TYPE_FILTER',

  SET_OPTIONS_ERROR = 'EMAIL_BUILDER/SET_OPTIONS_ERROR',
  SET_OPTIONAL_FILTERS_ERROR = 'EMAIL_BUILDER/SET_OPTIONAL_FILTERS_ERROR',
  SET_LAYOUT_SETTINGS_ERROR = 'EMAIL_BUILDER/SET_LAYOUT_SETTINGS_ERROR',
  SET_SOCIAL_INFORMATION_ERROR = 'EMAIL_BUILDER/SET_SOCIAL_INFORMATION_ERROR',

  SET_DATA_SOURCE_ID = 'EMAIL_BUILDER/SET_DATA_SOURCE_ID',
  SET_MEDIA_SOURCES = 'EMAIL_BUILDER/SET_MEDIA_SOURCES',

  SET_FILTER_UPDATING = 'EMAIL_BUILDER/SET_FILTER_UPDATING',

  // Panel actions
  SET_CURRENT_PANEL = 'EMAIL_BUILDER/SET_CURRENT_PANEL',
  SET_EMAIL_PREVIEW_TYPE = 'EMAIL_BUILDER/SET_EMAIL_PREVIEW_TYPE',

  // Data actions
  REQUEST_FANREELS = 'EMAIL_BUILDER/REQUEST_FANREELS',
  RECEIVE_FANREELS_SUCCEEDED = 'EMAIL_BUILDER/RECEIVE_FANREELS_SUCCEEDED',
  RECEIVE_FANREELS_FAILED = 'EMAIL_BUILDER/RECEIVE_FANREELS_FAILED',

  REQUEST_GALLERIES = 'EMAIL_BUILDER/REQUEST_GALLERIES',
  RECEIVE_GALLERIES_SUCCEEDED = 'EMAIL_BUILDER/RECEIVE_GALLERIES_SUCCEEDED',
  RECEIVE_GALLERIES_FAILED = 'EMAIL_BUILDER/RECEIVE_GALLERIES_FAILED',

  REQUEST_DATA_SOURCES = 'EMAIL_BUILDER/REQUEST_DATA_SOURCES',
  RECEIVE_DATA_SOURCES_SUCCEEDED = 'EMAIL_BUILDER/RECEIVE_DATA_SOURCES_SUCCEEDED',
  RECEIVE_DATA_SOURCES_FAILED = 'EMAIL_BUILDER/RECEIVE_DATA_SOURCES_FAILED',

  SET_REEL_ID_TO_DATA_SOURCE_ID_MAP = 'EMAIL_BUILDER/SET_REEL_ID_TO_DATA_SOURCE_ID_MAP',

  REQUEST_MEDIA_ITEMS = 'EMAIL_BUILDER/REQUEST_MEDIA_ITEMS',
  RECEIVE_MEDIA_ITEMS_SUCCEEDED = 'EMAIL_BUILDER/RECEIVE_MEDIA_ITEMS_SUCCEEDED',
  RECEIVE_MEDIA_ITEMS_FAILED = 'EMAIL_BUILDER/RECEIVE_MEDIA_ITEMS_FAILED',

  SET_ITEMS_MATCHING_FILTERS_COUNT = 'EMAIL_BUILDER/SET_ITEMS_MATCHING_FILTERS_COUNT',
  SET_ITEMS_IN_GALLERY_COUNT = 'EMAIL_BUILDER/SET_ITEMS_IN_GALLERY_COUNT',

  SET_MEDIA_ITEMS = 'EMAIL_BUILDER/SET_MEDIA_ITEMS',
  SET_ALL_MEDIA_ITEMS = 'EMAIL_BUILDER/SET_ALL_MEDIA_ITEMS',
  SET_AFTER_CURSOR = 'EMAIL_BUILDER/SET_AFTER_CURSOR',
  SET_DELETED_COUNT = 'EMAIL_BUILDER/SET_DELETED_COUNT',
  SET_EXTRA_ITEMS = 'EMAIL_BUILDER/SET_EXTRA_ITEMS',
  ADD_MEDIA_ITEMS = 'EMAIL_BUILDER/ADD_MEDIA_ITEMS',
  SET_INITIAL_FETCH = 'EMAIL_BUILDER/SET_INITIAL_FETCH',
  SET_END_OF_MEDIA_REACHED = 'EMAIL_BUILDER/SET_END_OF_MEDIA_REACHED',
  RESET_MEDIA_DATA = 'EMAIL_BUILDER/RESET_MEDIA_DATA',

  SET_STATIC_HTML = 'EMAIL_BUILDER/SET_STATIC_HTML',
}

export const TAB_TITLES = ['Layout', 'Optional Filters', 'Embed'];

export const DATA_SOURCE_NAME_PATTERN = '- Email Gallery Builder API';

export const TOP_MEDIA_API_REQUEST_LIMIT = 25;

export const USERNAME_CONTAINER_HEIGHT = 30;

export const IMAGE_SIZE = 600;

export const ERROR_TOOLTIP_TEXT =
  'There are errors in your customization options.' +
  ' Please correct them before continuing.';

export const MISSING_URL_TOOLTIP_TEXT = 'Please enter a URL before continuing.';

export const MEDIA_TYPE_FILTER_TOOLTIP_TEXT =
  'At least one media type must be selected.';

export const EMAIL_GALLERY_ARIA_LABEL_TEXT =
  'User-generated content from social media. To browse more user-generated content, select the media.';

export enum BUILDER_TYPES {
  EVERGREEN = 'Evergreen',
  CAMPAIGN = 'Campaign',
}

export enum EMAIL_PREVIEW_TYPE {
  MOBILE = 'mobile',
  DESKTOP = 'desktop',
}

export enum FONT_CHOICES {
  ARIAL = 'Arial',
  HELVETICA = 'Helvetica',
  TIMES_NEW_ROMAN = 'Times New Roman',
  TIMES = 'Times',
  COURIER_NEW = 'Courier New',
  COURIER = 'Courier',
  VERDANA = 'Verdana',
  GEORGIA = 'Georgia',
  PALATINO = 'Palatino',
  GARAMOND = 'Garamond',
  TREBUCHET_MS = 'Trebuchet MS',
  IMPACT = 'Impact',
  ARIAL_BLACK = 'Arial Black',
}

export enum IMAGE_LINK_DESTINATIONS {
  STATIC = 'Static URL',
  PRODUCT = 'Product URL',
}

export enum PRODUCT_OPTIONS {
  FIRST_PRODUCT = 'Link to first tagged product',
  HIGHER_PRODUCT = 'Link to highest priced product',
}

export enum MEDIA_TYPE {
  PHOTO = 'photo',
  VIDEO = 'video',
}

export const FONT_FAMILY_MAP = {
  [FONT_CHOICES.ARIAL]: `Arial,'Helvetica Neue',Helvetica,sans-serif`,
  [FONT_CHOICES.HELVETICA]: `'Helvetica Neue',Helvetica,Arial,sans-serif`,
  [FONT_CHOICES.TIMES_NEW_ROMAN]: `TimesNewRoman,'Times New Roman',Times,Baskerville,Georgia,serif`,
  [FONT_CHOICES.TIMES]: `Times,'TimesNewRoman','Times New Roman',Baskerville,Georgia,serif`,
  [FONT_CHOICES.COURIER_NEW]: `'Courier New',Courier,'Lucida Sans Typewriter','Lucida Typewriter',monospace`,
  [FONT_CHOICES.COURIER]: `Courier,'Courier New','Lucida Sans Typewriter','Lucida Typewriter',monospace`,
  [FONT_CHOICES.VERDANA]: `Verdana,Geneva,sans-serif`,
  [FONT_CHOICES.GEORGIA]: `Georgia,Times,'Times New Roman',serif`,
  [FONT_CHOICES.PALATINO]: `Palatino,'Palatino Linotype','Palatino LT STD','Book Antiqua',Georgia,serif`,
  [FONT_CHOICES.GARAMOND]: `Garamond,Baskerville,'Baskerville Old Face','Hoefler Text','Times New Roman',serif`,
  [FONT_CHOICES.TREBUCHET_MS]: `'Trebuchet MS','Lucida Grande','Lucida Sans Unicode','Lucida Sans',Tahoma,sans-serif`,
  [FONT_CHOICES.IMPACT]: `Impact,Haettenschweiler,'Franklin Gothic Bold',Charcoal,'Helvetica Inserat',
    'Bitstream Vera Sans Bold','Arial Black','sans serif'`,
  [FONT_CHOICES.ARIAL_BLACK]: `'Arial Black','Arial Bold',Gadget,sans-serif`,
};

export const DEFAULT_AVATAR_URL =
  'https://d28m5bx785ox17.cloudfront.net/v1/img/NBhuquSqA95xCTwMOglawt_gXL8qjzd_8MKeYq-PY0U=/b/50';

export enum FIELD_KEYS {
  BUILDER_TYPE = 'builderType',
  SELECTED_MEDIA_SOURCE = 'selectedMediaSource',
  IMAGE_LINK = 'imageLink',
  PRODUCT_OPTION = 'productOption',
  URL = 'url',

  // TODO: Delete once we've moved to the responsive inputs
  NUM_ROWS = 'numRows',
  NUM_COLUMNS = 'numColumns',
  IMAGE_WIDTH = 'imageWidth',
  IMAGE_HEIGHT = 'imageHeight',

  NUM_ROWS_DESKTOP = 'numRowsDesktop',
  NUM_COLUMNS_DESKTOP = 'numColumnsDesktop',
  USE_MOBILE_LAYOUT = 'useMobileLayout',
  NUM_ROWS_MOBILE = 'numRowsMobile',
  NUM_COLUMNS_MOBILE = 'numColumnsMobile',
  PADDING = 'padding',

  DISPLAY_USERNAME = 'displayUsername',
  BACKGROUND_COLOR = 'backgroundColor',
  BACKGROUND_OPACITY = 'backgroundOpacity',
  USERNAME_FONT = 'usernameFont',
  USERNAME_TEXT_COLOR = 'usernameTextColor',

  LABEL = 'label',
  PRODUCT_ID = 'productId',
  PRODUCT_URL = 'productUrl',
  CATEGORY = 'category',
  USERNAME = 'username',
  TAG = 'tag',
  ADDITIONAL_FILTERS = 'additionalFilters',
}

export const optionFields = {
  [FIELD_KEYS.BUILDER_TYPE]: {
    label: 'gallery type',
  },
  [FIELD_KEYS.SELECTED_MEDIA_SOURCE]: {
    label: 'media source',
  },
  [FIELD_KEYS.IMAGE_LINK]: {
    label: 'image links',
  },
  [FIELD_KEYS.PRODUCT_OPTION]: {
    label: 'product option',
  },
  [FIELD_KEYS.URL]: {
    label: 'gallery url',
    placeholder: 'Enter a url...',
  },
};

export const layoutDesktopFields = {
  [FIELD_KEYS.NUM_ROWS_DESKTOP]: {
    label: 'Rows (Desktop)',
    placeholder: 2,
  },
  [FIELD_KEYS.NUM_COLUMNS_DESKTOP]: {
    label: 'Columns (Desktop)',
    placeholder: 4,
  },
};

export const layoutMobileFields = {
  [FIELD_KEYS.NUM_ROWS_MOBILE]: {
    label: 'Rows (Mobile)',
    placeholder: 4,
  },
  [FIELD_KEYS.NUM_COLUMNS_MOBILE]: {
    label: 'Columns (Mobile)',
    placeholder: 2,
  },
};

// TODO: Delete once we've moved to the responsive inputs
export const layoutSettingsFields = {
  [FIELD_KEYS.NUM_ROWS]: {
    label: '# Rows',
    placeholder: 2,
  },
  [FIELD_KEYS.NUM_COLUMNS]: {
    label: '# Columns',
    placeholder: 3,
  },
  [FIELD_KEYS.IMAGE_WIDTH]: {
    label: 'Image width (px)',
    placeholder: 200,
  },
  [FIELD_KEYS.IMAGE_HEIGHT]: {
    label: 'Image height (px)',
    placeholder: 200,
  },
  [FIELD_KEYS.PADDING]: {
    label: 'Padding',
    placeholder: 0,
  },
};

export const socialInformationFields = {
  [FIELD_KEYS.DISPLAY_USERNAME]: {
    label: 'Display username',
  },
  [FIELD_KEYS.BACKGROUND_COLOR]: {
    label: 'background color',
    placeholder: '#ffffff',
  },
  [FIELD_KEYS.BACKGROUND_OPACITY]: {
    label: 'background opacity',
  },
  [FIELD_KEYS.USERNAME_FONT]: {
    label: 'username font',
  },
  [FIELD_KEYS.USERNAME_TEXT_COLOR]: {
    label: 'username text color',
    placeholder: '#000000',
  },
};

export const optionalFiltersFields = {
  [FIELD_KEYS.LABEL]: {
    label: 'label',
    placeholder: 'e.g. UGC',
  },
  [FIELD_KEYS.PRODUCT_ID]: {
    label: 'product id',
    placeholder: 'e.g. SKU0001',
  },
  [FIELD_KEYS.PRODUCT_URL]: {
    label: 'product url',
    placeholder: 'e.g. https://habiliment.co/',
  },
  [FIELD_KEYS.CATEGORY]: {
    label: 'category',
    placeholder: 'e.g. Footwear',
  },
  [FIELD_KEYS.USERNAME]: {
    label: 'username',
    placeholder: 'e.g. myusername',
  },
  [FIELD_KEYS.TAG]: {
    label: 'tag',
    placeholder: 'e.g. myhashtag',
  },
  [FIELD_KEYS.ADDITIONAL_FILTERS]: {
    label: 'additional filters',
    placeholder: `e.g. brand:'habiliment'`,
  },
};

export enum TAB_INDICES {
  LAYOUT = 1,
  FILTERS = 2,
  EMBED = 3,
}

export const generateTableStyles = (padding: number) => {
  return {
    borderCollapse: 'separate',
    borderSpacing: `${padding}px`,
    padding: 0,
    margin: '0 auto',
  } as const;
};

export const generateResponsiveTableStyles = (padding: number) => {
  return {
    padding: `${padding}px`,
    borderCollapse: 'separate',
    margin: '0 auto',
    textAlign: 'center',
    borderSpacing: 0,
  } as const;
};

export const dataStyles = {
  margin: 0,
  border: 0,
  position: 'relative',
} as const;

export const RESPONSIVE_IMAGE_STYLES = {
  width: '100%',
  maxWidth: IMAGE_SIZE,
  height: 'auto',
  border: 0,
};

export const rowStyles = {
  padding: 0,
  margin: 0,
  border: 0,
  lineHeight: 0,
};

export const anchorStyles = {
  padding: 0,
  margin: 0,
  display: 'inline-block',
  overflow: 'hidden',
  textDecoration: 'none',
};

export const generateResponsiveImageOuterTableStyles = (numColumns: number) => {
  const widthPercentage = truncateToFourDecimalPlaces(100 / numColumns);

  return {
    width: '100%',
    maxWidth: `${widthPercentage}%`,
    display: 'inline-block',
    verticalAlign: 'top',
    margin: '0 auto',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  } as const;
};

export const RESPONSIVE_IMAGE_INNER_TABLE_STYLES = {
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  borderSpacing: 0,
  borderCollapse: 'collapse',
} as const;

export const generateResponsiveImageOuterDataStyles = (padding: number) => {
  return {
    padding: `${padding}px`,
  };
};

export const RESPONSIVE_IMAGE_INNER_DATA_STYLES = {
  padding: 0,
};

export const RESPONSIVE_ROW_STYLES = {
  textAlign: 'center',
  fontSize: 0,
  padding: 0,
} as const;

export const HIDDEN_STYLES = {
  display: 'none',
  width: 0,
  height: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  msoHide: 'all',
} as const;

export const MSO_TABLE_STYLES = 'border-spacing:0';

export const MSO_DATA_STYLES = 'padding:0';

export const EMBED_CSS = `
  <style type="text/css">
    @media only screen and (max-width: 600px) {
      .cu-desktop-layout {
        display: none;
        width: 0;
        height: 0;
        overflow: hidden;
        visibility: hidden;
      }

      .cu-mobile-layout {
        display: table-row !important;
        width: auto !important;
        height: auto !important;
        overflow: visible !important;
        visibility: visible !important;
      }
    }
  </style>
`;

const emailBuilderTrackingPrefix = 'email-builder';

export const EmailBuilderTrackingIds = {
  LAYOUT_CUSTOMIZATION_SECTION_TRACKING_ID: `${emailBuilderTrackingPrefix}_layout-section`,

  MOBILE_TOGGLE_TRACKING_ID: `${emailBuilderTrackingPrefix}_mobile-toggle`,
  MOBILE_ROW_INPUT_TRACKING_ID: `${emailBuilderTrackingPrefix}_mobile-row-input`,
  MOBILE_COLUMN_INPUT_TRACKING_ID: `${emailBuilderTrackingPrefix}_mobile-column-input`,
  DESKTOP_ROW_INPUT_TRACKING_ID: `${emailBuilderTrackingPrefix}_desktop-row-input`,
  DESKTOP_COLUMN_INPUT_TRACKING_ID: `${emailBuilderTrackingPrefix}_desktop-column-input`,

  DRAGGABLE_IMAGE_TRACKING_ID: `${emailBuilderTrackingPrefix}_draggable-image`,
};
